import React from "react";
import { Button, TextField, Select, FormControl, InputLabel, MenuItem, CircularProgress, FormControlLabel, Checkbox } from "@material-ui/core";

import { LOADING_STATE, PAGE_ROUTES } from "../../utils/stateTypes";
import SEO_DATA from "../../utils/seoData";
import SeoHead from "../general/SeoHead";

const styles = {
  inputHalf: {
    width: "calc(50% - 10px)",
    marginBottom: 20,
    marginRight: 20,
    textAlign: "left",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  inputHalf2: {
    width: "calc(50% - 10px)",
    marginBottom: 20,
    textAlign: "left",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  input: {
    marginBottom: 20,
    textAlign: "left",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  checkboxInput: {
    marginBottom: 20,
    color: "#45474B",
  },
  submit: {
    padding: "15px",
  },
};

const sendMessage = (setSendState) => (
  async (e) => {
    e.preventDefault();
    setSendState(LOADING_STATE.LOADING);

    //Get the form data
    const formData = new FormData(e.target);
    //Create a JSON object from the form data
    const data = {};
    formData.forEach((value, key) => {
      data[key] = value;
    });
    data.Signup_Newsletter = (data.Signup_Newsletter === "");

    const response = await fetch("/api/Store/SubmitMealPlanForm", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    if (response.ok) {
      setSendState(LOADING_STATE.LOADED);
    }
    else {
      setSendState(LOADING_STATE.ERROR);
    }
  }
);


const MealPlanForm = () => {
  const [sendState, setSendState] = React.useState(LOADING_STATE.NOT_LOADING);

  let submitLabel = "Submit";
  if (sendState === LOADING_STATE.LOADING) {
    submitLabel = <CircularProgress size={25} />;
  }
  else if (sendState === LOADING_STATE.LOADED) {
    submitLabel = "Sent! Keep an eye on your inbox";
  }
  else if (sendState === LOADING_STATE.ERROR) {
    submitLabel = "Retry";
  }


  return (
    <main>
      <SeoHead
        {...SEO_DATA[PAGE_ROUTES.MEAL_PLAN_FORM]}
        path={PAGE_ROUTES.MEAL_PLAN_FORM}
      />
      
      <header className={"page-header standard-width"}>
        <h1 className={"section-head"}>Meal Plan Questionnaire</h1>
        <p className={"meal-plan-form-intro"}>
          Thank you for your purchase! Please fill out the form below so I can create a meal plan that is tailored to your needs, which
          will be sent to you within 1 week. The more information
          you give us the better 🍋🍋
        </p>
      </header>

      <section className={"meal-plan-form-section standard-width"}>
        <form
          className={"meal-plan-form standard-box"}
          onSubmit={sendMessage(setSendState)}
        >
          <TextField
            label={"First Name"}
            name={"First_Name"}
            variant={"filled"}
            color={"secondary"}
            style={styles.inputHalf}
            required
          />
          <TextField
            label={"Surname"}
            name={"Surname"}
            variant={"filled"}
            color={"secondary"}
            style={styles.inputHalf2}
            required
          />
          <TextField
            label={"Phone"}
            name={"Phone"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            required
          />
          <TextField
            label={"Email"}
            name={"Email"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            required
          />
          <FormControl
            variant={"filled"}
            color={"secondary"}
            style={styles.inputHalf}
          >
            <InputLabel required>Your location</InputLabel>
            <Select
              MenuProps={{
                disableScrollLock: true,
              }}
              color={"secondary"}
              name={"Location"}
              required
            >
              <MenuItem
                value={"Johannesburg"}
                style={styles.menuItem}
              >
                Johannesburg
              </MenuItem>
              <MenuItem
                value={"Cape Town"}
                style={styles.menuItem}
              >
                Cape Town
              </MenuItem>
              <MenuItem
                value={"Other"}
                style={styles.menuItem}
              >
                Other
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl
            variant={"filled"}
            color={"secondary"}
            style={styles.inputHalf2}
          >
            <InputLabel required>Gender</InputLabel>
            <Select
              MenuProps={{
                disableScrollLock: true,
              }}
              color={"secondary"}
              name={"Gender"}
              required
            >
              <MenuItem
                value={"Male"}
                style={styles.menuItem}
              >
                Male
              </MenuItem>
              <MenuItem
                value={"Female"}
                style={styles.menuItem}
              >
                Female
              </MenuItem>
              <MenuItem
                value={"Other"}
                style={styles.menuItem}
              >
                Other
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={"Age"}
            name={"Age"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            required
          />
          <TextField
            label={"What is your current weight (kg)"}
            name={"Weight"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            required
          />
          <TextField
            label={"What is your current height (cm)"}
            name={"Height"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            required
          />
          <TextField
            label={"Do you have an medical conditions (if so, please specify)"}
            name={"Medical_Conditions"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            required
          />
          <TextField
            label={"Are you on any medications (if so, please specify)"}
            name={"Medications"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            required
          />
          <TextField
            label={"What is your family medical history?"}
            name={"Family_Medical_History"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            multiline
            fullWidth
            required
          />
          <TextField
            label={"Do you have any allergies (if so, please specify)"}
            name={"Allergies"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            required
          />
          <div className={"lifestyle-pre-question"}>
            <h3>Lifestyle categories</h3>
            <ul>
              <li><b>Sedentary lifestyle</b> - Work in an office or sit at a desk for more than 8 hours a day without doing physical activity</li>
              <li><b>Low active</b> - Mobilising for at least 3 hours a day. Or exercising (cardio) once to twice a week for 30 minutes</li>
              <li><b>Active</b> - Mobilising for most of the day, or exercising for 30 minutes or more for three times a week</li>
              <li><b>Very active</b> - Mobilising throughout the day with exercise for 30 minutes or more 4 to 5 times a week.</li>
            </ul>
          </div>
          <FormControl
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
          >
            <InputLabel required>What type of lifestyle do you lead?</InputLabel>
            <Select
              MenuProps={{
                disableScrollLock: true,
              }}
              color={"secondary"}
              name={"Lifestyle_Type"}
              required
            >
              <MenuItem
                value={"Sedentary"}
                style={styles.menuItem}
              >
                Sedentary
              </MenuItem>
              <MenuItem
                value={"Low Active"}
                style={styles.menuItem}
              >
                Low Active
              </MenuItem>
              <MenuItem
                value={"Active"}
                style={styles.menuItem}
              >
                Active
              </MenuItem>
              <MenuItem
                value={"Very Active"}
                style={styles.menuItem}
              >
                Very Active
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={"What is your desired outcome?"}
            name={"Desired_Outcome"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            placeholder={"E.g. weight loss, less fatigue, more energy, etc."}
            multiline
            fullWidth
            required
          />
          <div className={"lifestyle-pre-question"}>
            <h3>Recommended macronutrient distrution</h3>
            <ul>
              <li><b>Carbohydrates</b> - 45% - 65%</li>
              <li><b>Protein</b> - 10% to 30%</li>
              <li><b>Fat</b> - 20% - 30%</li>
            </ul>
            <p>Macro distribution is optional to complete. If you are happy for me to allocate this for you, please leave out the following 3 fields.</p>
          </div>
          <TextField
            label={"How would you like to distribute your macronutrients - Carbohydrates"}
            name={"Macronutrients_Carbs"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            placeholder={"Optional - Provide a percentage (e.g. 50%)"}
            fullWidth
          />
          <TextField
            label={"How would you like to distribute your macronutrients - Protein"}
            name={"Macronutrients_Protein"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            placeholder={"Optional - Provide a percentage (e.g. 25%)"}
            fullWidth
          />
          <TextField
            label={"How would you like to distribute your macronutrients - Fat"}
            name={"Macronutrients_Fat"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            placeholder={"Optional - Provide a percentage (e.g. 25%)"}
            fullWidth
          />
          <TextField
            label={"Where do you do your grocery shopping?"}
            name={"Grocery_Stores"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            placeholder={"E.g. Woolworths, Checkers, etc."}
            fullWidth
            required
          />
          <TextField
            label={"Do you have any work patterns that affect your meal times and dietary intakes?"}
            name={"Work_Patterns"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            multiline
            required
          />
          <TextField
            label={"Do you have any weekend patterns that affect your dietary intakes regularly?"}
            name={"Weekend_Patterns"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            multiline
            required
          />
          <TextField
            label={"What are social events that you find affects your dietary intake regularly"}
            name={"Social_Events"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            multiline
            required
          />
          <TextField
            label={"Do you have any religious factors that affect your dietary intake regularly"}
            name={"Religious_Factors"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            multiline
            required
          />
          <TextField
            label={"What do you feel has stopped you from reaching your desired weight / dietary goals previously?"}
            name={"Previous_Hurdles"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            multiline
            required
          />
          <TextField
            label={"What I eat for breakfast"}
            name={"Breakfast"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            multiline
            required
          />
          <TextField
            label={"What I eat for morning snacks"}
            name={"Morning_Snacks"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            multiline
            required
          />
          <TextField
            label={"What I eat for lunch"}
            name={"Lunch"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            multiline
            required
          />
          <TextField
            label={"What I eat for afternoon snacks"}
            name={"Afternoon_Snacks"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            multiline
            required
          />
          <TextField
            label={"What I eat for dinner"}
            name={"Dinner"}
            variant={"filled"}
            color={"secondary"}
            style={styles.input}
            fullWidth
            multiline
            required
          />
          <FormControlLabel
            control={<Checkbox name={"Signup_Newsletter"} />}
            label={"I would like to sign up for the weekly newsletter"}
            style={styles.checkboxInput}
          />
          <Button
            variant={"contained"}
            color={"primary"}
            type={"submit"}
            fullWidth
            style={styles.submit}
            disabled={(sendState === LOADING_STATE.LOADING || sendState === LOADING_STATE.LOADED)}
          >
            {submitLabel}
          </Button>
        </form>
      </section>
    </main>
  );
};

export default MealPlanForm;

